import { useEffect, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../auth/AuthProvider.js';
import { getDashboard, getMyPendingApprovals } from '../../api/workflowApi.js';
import { sx } from './wfKit.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import { DataTable } from '../../../../shared/components/index.js';
import { useBusinessDay } from '../../../../shared/hooks/useBusinessDay.js';

/**
 * WorkflowDashboardPage — workflow KPIs + "my pending approvals" (M04 §L
 * dashboard / analytics coverage).
 *
 * Reads `/v1/wf/dashboard` (aggregate KPIs) and `/v1/wf/my-pending-approvals`
 * (the caller's own queue, user resolved from the JWT). The dashboard payload
 * shape is backend-defined; KPI cards render defensively from whatever scalar
 * entries the aggregate returns.
 */
interface PendingRow {
  readonly instanceId?: string;
  readonly instanceRefNumber?: string;
  readonly entityType?: string;
  readonly workflowName?: string;
  readonly submittedAt?: string;
  readonly [k: string]: unknown;
}

function scalarEntries(data: unknown): Array<[string, string]> {
  if (!data || typeof data !== 'object') return [];
  return Object.entries(data as Record<string, unknown>)
    .filter(([, v]) => typeof v === 'number' || typeof v === 'string')
    .map(([k, v]) => [k, String(v)]);
}

export function WorkflowDashboardPage(): ReactNode {
  const { token } = useAuth();
  const { t } = useTranslation();
  // Instants follow the tenant's DISPLAY_DATE_FORMAT, not the reader's language picker.
  const { at } = useBusinessDay();
  const [kpis, setKpis] = useState<Array<[string, string]> | null>(null);
  const [pending, setPending] = useState<PendingRow[] | null>(null);
  const [pendingTotal, setPendingTotal] = useState(0);
  const [error, setError] = useState<string | null>(null);

  // The KPI set is backend-defined, so a card is labelled from the catalogue
  // when a string exists and falls back to the raw key otherwise — the same
  // defensive posture as scalarEntries, and never a dotted key in the UI.
  const kpiLabel = (key: string): string => {
    const translated = t(`wf.dashboard.kpi.${key}`);
    return translated === `wf.dashboard.kpi.${key}` ? key : translated;
  };

  useEffect(() => {
    let alive = true;
    getDashboard(token)
      .then((r) => { if (alive) setKpis(scalarEntries(r.data)); })
      .catch((e) => { if (alive) setError((e as Error).message); });
    getMyPendingApprovals(token, { page: 1, pageSize: 20 })
      .then((r) => { if (alive) { setPending((r.data as PendingRow[]) ?? []); setPendingTotal(r.meta?.total ?? 0); } })
      .catch((e) => { if (alive) setError((e as Error).message); });
    return () => { alive = false; };
  }, [token]);

  return (
    <main aria-label="wf-dashboard" style={sx.main}>
      <div style={sx.headerRow}>
        <h1>{t('wf.dashboard.title')}</h1>
        <Link to="/admin/workflows" style={sx.secondaryBtn}>{t('wf.common.designer')}</Link>
      </div>

      {error && <p role="alert" style={sx.error}>{error}</p>}

      <section aria-label="kpi-cards" style={{ marginBottom: 24 }}>
        <h2 style={{ fontSize: 16 }}>{t('wf.dashboard.keyMetrics')}</h2>
        {!kpis ? <p>{t('common.loading')}</p> : kpis.length === 0 ? (
          <p data-testid="kpis-empty" style={{ color: 'var(--color-text-muted)' }}>{t('wf.dashboard.noMetrics')}</p>
        ) : (
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
            {kpis.map(([k, v]) => (
              <div key={k} data-testid={`kpi-${k}`} style={{ ...sx.card, minWidth: 140, marginBottom: 0 }}>
                <div style={{ fontSize: 24, fontWeight: 700 }}>{v}</div>
                <div style={{ fontSize: 12, color: 'var(--color-text-muted)' }}>{kpiLabel(k)}</div>
              </div>
            ))}
          </div>
        )}
      </section>

      <section aria-label="my-pending-approvals">
        <h2 style={{ fontSize: 16 }}>{t('wf.dashboard.myPending', { count: pendingTotal })}</h2>
        {!pending ? <p>{t('common.loading')}</p> : pending.length === 0 ? (
          <p data-testid="pending-empty" style={{ color: 'var(--color-text-muted)' }}>{t('wf.dashboard.nothingPending')}</p>
        ) : (
          <DataTable
            ariaLabel="pending-table"
            rows={pending.map((p, i) => ({ ...p, _rowKey: p.instanceId ?? String(i) }))}
            rowKey={(p) => p._rowKey}
            rowTestId={(p) => `pending-${p._rowKey}`}
            columns={[
              { key: 'reference', header: t('wf.dashboard.colReference'), render: (p) => p.instanceRefNumber ?? p.instanceId ?? '—' },
              { key: 'entity', header: t('wf.common.entity'), render: (p) => p.entityType ?? '—' },
              { key: 'workflow', header: t('wf.dashboard.colWorkflow'), render: (p) => p.workflowName ?? '—' },
              { key: 'submitted', header: t('wf.common.submitted'), render: (p) => (p.submittedAt ? at(p.submittedAt) : '—') },
              { key: 'actions', header: '', render: (p) => (p.instanceId ? <Link to={`/admin/wf/instances/${p.instanceId}`} style={sx.link}>{t('wf.dashboard.open')}</Link> : null) },
            ]}
          />
        )}
      </section>
    </main>
  );
}
